import React, { useState } from 'react';

export interface FolderStat {
  value: string;
  label: string;
  detail: string;
  footnote?: string;
}

interface FolderStatStackProps {
  stats: FolderStat[];
}

const FolderStatStack: React.FC<FolderStatStackProps> = ({ stats }) => {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <div style={{
      maxWidth: '760px',
      margin: '0 auto',
      borderRadius: 'var(--radius-xl)',
      overflow: 'hidden',
      boxShadow: 'var(--shadow-2xl)',
    }}>
      {stats.map((stat, i) => {
        const isExpanded = expanded === i;
        const mixPct = 100 - i * 10;
        const bg = i === 0
          ? 'var(--color-primary)'
          : `color-mix(in srgb, var(--color-primary) ${mixPct}%, #000)`;

        return (
          <div
            key={i}
            onMouseEnter={() => setExpanded(i)}
            onMouseLeave={() => setExpanded(null)}
            style={{
              background: bg,
              color: '#fff',
              position: 'relative',
              borderTop: i > 0 ? '2px solid rgba(0,0,0,0.3)' : 'none',
              transition: 'transform 250ms ease, box-shadow 250ms ease',
              transform: isExpanded ? 'translateY(-3px)' : 'none',
              zIndex: isExpanded ? 10 : stats.length - i,
              boxShadow: isExpanded ? '0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)' : 'none',
              cursor: 'default',
            }}
          >
            {/* Left tab accent — folder spine */}
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '5px',
              background: isExpanded ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)',
              transition: 'background 250ms ease',
            }} />

            {/* Header row — always visible */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-6)',
              padding: 'var(--spacing-5) var(--spacing-8) var(--spacing-5) calc(var(--spacing-8) + 5px)',
            }}>
              <span style={{
                fontSize: 'clamp(1.75rem, 3vw, 2.75rem)',
                fontWeight: 800,
                lineHeight: 1,
                minWidth: '100px',
                flexShrink: 0,
                color: '#fff',
              }}>
                {stat.value}
              </span>
              <span style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.85)',
                letterSpacing: '0.02em',
                flex: 1,
              }}>
                {stat.label}
              </span>
              <svg
                width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" aria-hidden="true"
                style={{
                  flexShrink: 0,
                  opacity: isExpanded ? 0.9 : 0.35,
                  transform: isExpanded ? 'rotate(90deg)' : 'none',
                  transition: 'transform 250ms ease, opacity 200ms ease',
                }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
              </svg>
            </div>

            {/* Expandable detail */}
            <div style={{
              maxHeight: isExpanded ? '160px' : '0',
              overflow: 'hidden',
              transition: 'max-height 350ms ease',
            }}>
              <div style={{
                padding: '0 var(--spacing-8) var(--spacing-6)',
                paddingLeft: 'calc(var(--spacing-8) + 100px + var(--spacing-6) + 5px)',
              }}>
                <div style={{
                  borderTop: '1px solid rgba(255,255,255,0.15)',
                  paddingTop: 'var(--spacing-4)',
                }}>
                  <p style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'rgba(255,255,255,0.78)',
                    lineHeight: 'var(--line-height-relaxed)',
                    margin: 0,
                  }}>
                    {stat.detail}
                  </p>
                  {stat.footnote && (
                    <p style={{
                      marginTop: 'var(--spacing-2)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'rgba(255,255,255,0.45)',
                      margin: 'var(--spacing-2) 0 0',
                    }}>
                      {stat.footnote}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FolderStatStack;
